import {
  Box,
  HStack,
  Heading,
  Image,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import {HaliRenderOne, useHalState} from '@synako/haligator';
import {State} from '@synako/halitrek';
import {ItemCatalogue2Desc, LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {isItemCatalogueMarketplace} from 'ff-common';
import {useTranslation} from 'react-i18next';
import {useClient} from '@/haligator-factory';
import {PrixBlockUnitaire} from '@/domains/cadencier/components/PrixBlockUnitaire';
import {MarketplaceBuyButton} from '@/domains/produit/MarketplaceBuyButton';
import {SelectUnite} from '@/domains/panier/BuyProduct/SelectUnite';
import {ArticleCodesDisplay} from '@/domains/panier/components/ArticleCodesDisplay';
import {FicheProduitPdfRow} from './FicheProduit';

export const ProduitMarketplaceResume: React.FC<{
  itemState: State<ItemCatalogue2Desc>;
  prixHT: number;
  tva: number;
  ligneDePanier?: State<LigneDePanierDesc>;
  marketplaceLigneDePanier?: State<LigneDePanierDesc>;
}> = ({itemState, prixHT, tva, ligneDePanier, marketplaceLigneDePanier}) => {
  const data = itemState.data;
  const client = useClient();
  const {t} = useTranslation();
  const {data: marketplaceData} = useHalState(
    client.follow('go:marketplace', {marketplace_id: data.marketplace_id}),
  );

  if (!isItemCatalogueMarketplace(data)) {
    return null;
  }

  const logoSrc = marketplaceData?.logo_horizontal_couleur
    ? `${import.meta.env['VITE_MEDIA_URL']}${marketplaceData.logo_horizontal_couleur}`
    : undefined;

  return (
    <VStack
      m='20px'
      alignItems='left'
      // mb="50px"
      p={{base: '10px 20px 0px 20px', md: ''}}
    >
      {/* Référence produit */}
      <ArticleCodesDisplay
        code_article={data.code}
        marketplace_id={data.marketplace_id}
      />

      {/* Nom du produit */}
      <Heading
        as='h4'
        size={{base: 'sm', md: 'md'}}
        mt='10px'
      >
        {data.libelle}
      </Heading>

      {logoSrc && (
        <Image
          src={logoSrc}
          alt={marketplaceData?.nom ?? data.marketplace_id}
          h='52px'
          objectFit='contain'
          alignSelf='flex-start'
          mt='8px'
        />
      )}

      {/* Description du produit */}
      {data.avantages_produit && data.avantages_produit.length > 0 && (
        <>
          <Text
            fontSize='lg'
            fontWeight='bold'
            mt={2}
          >
            {t('fiche_produit.description')}
          </Text>
          <UnorderedList
            pl='2'
            spacing={1}
          >
            {data.avantages_produit.map((avantage, i) => (
              <ListItem
                key={i}
                fontSize='sm'
              >
                {avantage}
              </ListItem>
            ))}
          </UnorderedList>
        </>
      )}

      {/* Prix */}
      <VStack
        width='100%'
        alignItems='flex-start'
        gap='0'
        mt='10px'
        mb={{base: '20px', md: '10px'}}
      >
        <PrixBlockUnitaire
          prixUnitaire={prixHT}
          prixUnitaireTtc={itemState.data.prix_unitaire_ttc}
          uniteFacturation='PI'
          tauxTVA={tva}
          style={{mb: -1, mt: 0, minW: 0}}
          fontSizeMain='2xl'
          fontSizeSecondary='sm'
        />
      </VStack>
      <HStack
        alignItems='end'
        width='100%'
        flexWrap='wrap'
        bg='gray.75'
        padding='15px'
        borderRadius='8px'
        mt={{base: '15px', md: 0}}
      >
        <Box
          flex='1'
          border='0.5px'
          borderRadius='8px'
        >
          <SelectUnite
            ic={itemState.data}
            value='unitaire'
            w='100%'
            bg='white'
          />
        </Box>
        <Box
          flex='1'
          minW={0}
          position='relative'
        >
          <MarketplaceBuyButton
            itemState={itemState}
            ligneDePanier={ligneDePanier}
            marketplaceLigneDePanier={marketplaceLigneDePanier}
            context='fiche_produit'
            w='100%'
            minW='100px'
            mt='0'
          />
        </Box>
      </HStack>

      {/* Fiche produit PDF */}
      <HaliRenderOne
        nav={itemState.follow('fiche_technique_marketplace')}
        skeleton={() => (
          <FicheProduitPdfRow
            label={t(
              'fiche_produit.fiche_produit_pdf.telecharger_fiche_produit',
            )}
            mt='10px'
          />
        )}
        render={({data: ft}) => (
          <FicheProduitPdfRow
            url={ft.url}
            label={t(
              'fiche_produit.fiche_produit_pdf.telecharger_fiche_produit',
            )}
            mt='10px'
          />
        )}
        notFound={() => (
          <FicheProduitPdfRow
            label={t(
              'fiche_produit.fiche_produit_pdf.fiche_produit_non_disponible',
            )}
            mt='10px'
          />
        )}
      />
    </VStack>
  );
};
